import { useContext } from 'react'
import { useSearchParam } from 'react-use'
import { func } from 'prop-types'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'
import useCart from '../../../hooks/useCart'
import useFeatureFlags from '../../../hooks/useFeatureFlags'
import useAuthentication from '../../../hooks/useAuthentication'
import { buildErrorCodes, checkIsCartValid } from '../../../utils/cartValidations'
import {
  onItemRemovalFailure,
  onResolvingOfferError,
  onInvalidCartError
} from '../../../utils/legacyAnalytics'
import ProvinceMisMatchNotification from './ProvinceMisMatchNotification'
import TOSBreachNotification from './TOSBreachNotification'
import IMEIDuplicatedNotification from './IMEIDuplicatedNotification'
import ResolvingOfferNotification from './ResolvingOfferNotification'
import InvalidCartNotification from './InvalidCartNotification'
import IneligibleMHNotification from './IneligibleMHNotification'
import CallAgentNotification from './CallAgentNotification'
import CartItemRemovalError from './CartItemRemovalError'
import useMessages from '../../../hooks/useMessages'
import useLegacyAnalytics from '../../../hooks/useLegacyAnalytics'
import { RemainingAFBalanceNotification } from './RemainingAFBalanceNotification'
import { shouldShowRemainingAFBalanceReminder } from './util'
import OutOfStockNotification from './OutOfStockNotification'
import { PromoCodeNotification } from './PromoCodeNotification'
import { StyledBox, StyledContainer } from './styles'

const AppNotification = ({ setHasVisitorStateCookie }) => {
  const translate = useTranslate()
  const linkType = useSearchParam('linktype')
  const isAuthenticated = useAuthentication()
  const { prov, lang } = useContext(localeContext)
  const { updateLegacyDataLayer } = useLegacyAnalytics()

  const {
    cart,
    resolvingOfferMessage,
    isFetchingResolvingOffer,
    removeItems,
    resetCart,
    getCartItemsRemoveError,
    getCachedRedeemedCode,
    getRemoveCodeSucess
  } = useCart()
  const cartValidationStatus = checkIsCartValid({ cart, prov, linkType })
  const { messages } = useMessages()
  const { data: featureFlags } = useFeatureFlags()

  const renderNotifications = () => {
    // If still fetching the resolving offer, to avoid flickering
    // we will render nothing to the user
    if (isFetchingResolvingOffer) {
      return null
    }
    const notifications = [
      <CallAgentNotification
        key="CallAgentNotification"
        cart={cart}
        translate={translate}
        featureFlags={featureFlags}
        lang={lang}
        setHasVisitorStateCookie={setHasVisitorStateCookie}
      />
    ]

    if (!cartValidationStatus.isValid) {
      if (cartValidationStatus?.reasons?.includes('INVALID_PRODUCTS')) {
        const errorCodes = buildErrorCodes(cart)
        updateLegacyDataLayer(onInvalidCartError(errorCodes))
      }

      cartValidationStatus?.reasons?.forEach((reason) => {
        switch (reason) {
          case 'PROVINCE_MISMATCH':
            notifications.push(<ProvinceMisMatchNotification cart={cart} resetCart={resetCart} />)
            break
          case 'INELIGIBLE_MH':
            notifications.push(<IneligibleMHNotification translate={translate} />)
            break
          case 'RETURN_DEVICES_DUPLICATE_IMEI_FOUND':
            notifications.push(<IMEIDuplicatedNotification translate={translate} />)
            break
          case 'TOS_BREACH':
            notifications.push(<TOSBreachNotification translate={translate} />)
            break
          case 'OUT_OF_STOCK':
            notifications.push(
              <OutOfStockNotification
                key="OutOfStockNotification"
                cart={cart}
                translate={translate}
              />
            )
            break
          case 'INVALID_PRODUCTS':
            // When a resolving offer message is available, do not show the generic
            // invalid cart banner
            if (resolvingOfferMessage) {
              updateLegacyDataLayer(onResolvingOfferError)
              notifications.push(
                <ResolvingOfferNotification
                  key="ResolvingOfferNotification"
                  resolvingOfferMessage={resolvingOfferMessage}
                />
              )
            } else {
              notifications.push(
                <InvalidCartNotification
                  cart={cart}
                  lang={lang}
                  translate={translate}
                  isAuthenticated={isAuthenticated}
                  removeItems={removeItems}
                  resetCart={resetCart}
                />
              )
            }
            break
          default:
            break
        }
      })
    }

    if (getCachedRedeemedCode()) {
      notifications.push(
        <PromoCodeNotification promoCode={getCachedRedeemedCode()} currentEvent="applyPromoCode" />
      )
    } else if (getRemoveCodeSucess()) {
      notifications.push(
        <PromoCodeNotification promoCode={getRemoveCodeSucess()} currentEvent="removePromoCode" />
      )
    }

    if (shouldShowRemainingAFBalanceReminder(cart)) {
      notifications.push(<RemainingAFBalanceNotification translate={translate} lang={lang} />)
    }

    return notifications
  }

  // Render all the messages from the global message state
  const renderMessages = () => messages.map((m) => <StyledBox key={m.id}>{m.node}</StyledBox>)

  const renderErrors = () => {
    const cartItemRemovalError = getCartItemsRemoveError()
    if (cartItemRemovalError) {
      updateLegacyDataLayer(onItemRemovalFailure(cartItemRemovalError?.status))
      return <CartItemRemovalError translate={translate} />
    }
    return null
  }

  const notifications = renderNotifications()
  const mappedNotifications = notifications?.map((module) => (
    <StyledBox key={module.type.name}>{module}</StyledBox>
  ))

  const errors = renderErrors()
  return (
    <StyledContainer>
      {messages.length > 0 && renderMessages()}
      {notifications && mappedNotifications}
      {errors && <StyledBox>{renderErrors()}</StyledBox>}
    </StyledContainer>
  )
}

export default AppNotification

AppNotification.propTypes = {
  setHasVisitorStateCookie: func.isRequired
}
